import { useEffect } from 'react'
import { AlertOctagon } from 'lucide-react'
import ModalPortal from './ModalPortal'
import { useIsFlaggedForAbuse } from '../hooks/useAdminMode'
import './AbuseOverlay.css'

// The email a flagged user is asked to contact to appeal the lockout. Kept as a constant so the copy
// and the mailto link stay in sync.
const APPEAL_EMAIL = 'tom.daly@replay.io'

/**
 * A full-screen, non-dismissable lockout shown to any user an admin has flagged for abuse (see
 * netlify/functions/lib/abuse.ts; the flag is surfaced on the user via /api/auth-me). It is rendered
 * once at the app root (App.tsx) so it covers EVERY route — the app shell, the embedded iframe shell,
 * and the invite landing alike.
 *
 * The overlay is deliberately impossible to escape from the UI:
 *   • it portals to <body> at a z-index above all app chrome and covers the whole viewport, so nothing
 *     behind it is clickable;
 *   • it has no close button, no backdrop-click handler, and no escape-key handler; and
 *   • while mounted it disables page scrolling and pulls keyboard focus onto itself so tab/keyboard
 *     interaction can't reach the (inert, but still present) app underneath.
 *
 * This is a client-side lockout for the human at the keyboard — it is NOT the security boundary. The
 * server independently refuses to act on a flagged user (e.g. the GitHub App stops processing their
 * projects' webhooks; see findProjectsByRepo).
 */
export default function AbuseOverlay() {
  const flagged = useIsFlaggedForAbuse()

  // While the lockout is up, freeze background scrolling. Restored on unmount (i.e. if the account is
  // un-flagged and auth-me refreshes). No-op when not flagged.
  useEffect(() => {
    if (!flagged) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [flagged])

  if (!flagged) return null

  return (
    <ModalPortal>
      <div
        className="abuse-overlay"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="abuse-overlay-title"
        data-testid="abuse-overlay"
        // Grab focus so keyboard navigation stays trapped on the lockout rather than reaching the app
        // behind it. autoFocus is unreliable for a div, so focus it on mount via a ref callback.
        tabIndex={-1}
        ref={(el) => el?.focus()}
      >
        <div className="abuse-overlay__card">
          <AlertOctagon size={48} strokeWidth={1.75} className="abuse-overlay__icon" aria-hidden />
          <h1 id="abuse-overlay-title" className="abuse-overlay__title">
            Your account has been flagged for abuse
          </h1>
          <p className="abuse-overlay__body">
            If you are using Replay QA legitimately, please send an email to{' '}
            <a className="abuse-overlay__email" href={`mailto:${APPEAL_EMAIL}`}>
              {APPEAL_EMAIL}
            </a>{' '}
            explaining why your account should be restored.
          </p>
        </div>
      </div>
    </ModalPortal>
  )
}
